import { C, inp } from "./styles.js";
import { SECTION_DEFS, LIDAR_MATRIX, LIDAR_MODEL, DRONE_MODEL, DRONE_MODEL_VERSION, CRM_INTERNAL_EMAILS, SHEET_EQUIPMENT } from "./constants.js";
import { quoteEquipmentIds } from "./holds.js";
import { DEFAULT_QUOTE_DELIVERABLE_TEXT, getJobDateRange, fmtMoney, seededLinkedContacts } from "./utils.js";
import { matchContactsToCRM, recordEmails, emailDomain, isOrgDomain, resolveClient, normalizeName } from "./identity.js";
import { isProBono } from "./proBono.js";
import { primaryContactEmail, primaryContactPhone, confirmedPrimaryMatch, quoteHasLinkedContact } from "./quoteParty.js";
import { customPaymentSentence, moneyFingerprint, wordingOf, wordingOr } from "./printWording.js";

/* ═══ IMAGE COMPRESSION ═══ */
export const MAX_IMG_DIM = 1600;
export const IMG_QUALITY = 0.8;
export const compressImage = (file) => new Promise((resolve) => {
  const img = new Image();
  const objUrl = URL.createObjectURL(file);
  img.onload = () => {
    URL.revokeObjectURL(objUrl);
    let { width: w, height: h } = img;
    if (w <= MAX_IMG_DIM && h <= MAX_IMG_DIM && file.size < 500_000) {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(file);
      return;
    }
    if (w > h) { if (w > MAX_IMG_DIM) { h = Math.round(h * MAX_IMG_DIM / w); w = MAX_IMG_DIM; } }
    else { if (h > MAX_IMG_DIM) { w = Math.round(w * MAX_IMG_DIM / h); h = MAX_IMG_DIM; } }
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    resolve(canvas.toDataURL("image/jpeg", IMG_QUALITY));
  };
  img.onerror = () => { URL.revokeObjectURL(objUrl); resolve(null); };
  img.src = objUrl;
});

/* ═══ ATTACHMENTS ═══ */
// Durable-storage cap for quote attachments. Files larger than this are rejected
// at attach time (warned, not uploaded) — scoped to quote generation only.
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25 MB
export const isAttachmentTooLarge = (size) => Number(size) > MAX_ATTACHMENT_BYTES;

// True for both the normalized "image" category and raw "image/*" MIME types
// (website-submitted attachments arrive with the MIME type before normalization).
export const isImageAttachment = (type) =>
  type === "image" || (typeof type === "string" && type.startsWith("image/"));

// Categorize a picked File into the attachment `type` the quote schema stores.
export const attachmentTypeFor = (file) => {
  if (!file) return "file";
  if ((file.type || "").startsWith("image/")) return "image";
  const ext = (file.name || "").split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "pdf";
  if (ext === "doc" || ext === "docx") return "doc";
  return "file";
};

// The saved record behind a quote picked earlier, as it is now.
export const latestSavedQuote = (savedQuotes, picked) =>
  (picked && (savedQuotes || []).find((q) => q.id === picked.id)) || picked;

// The reference images that print on the client PDF addendum: images explicitly
// marked includeInAddendum. Single source of truth shared by builder + PDF.
export const addendumImages = (attachments) =>
  (attachments || []).filter(a => isImageAttachment(a?.type) && a?.includeInAddendum);

/* ═══ CONTACT LINKING (F8/F14) ═══ */
// Contacts store emails as an ARRAY; clients store a single email STRING. One
// accessor so UI surfaces don't each re-encode that shape difference (the same
// duality matchContactsToCRM handles for matching).
// The contact readers live in lib/quoteParty.js (one reader per fact); re-exported here
// because most of the builder imports them from this module.
export { primaryContactEmail, primaryContactPhone, confirmedPrimaryMatch, quoteHasLinkedContact } from "./quoteParty.js";

// Persist a new CRM contact, then decide how the quote's contact fields should be
// patched. The link (contactEntityId) is set ONLY when the save actually succeeds —
// otherwise the quote would hold a DANGLING id (the linked chip wouldn't resolve and
// BLANK_JOB would auto-link a booked job to a phantom contact). On failure (save throws)
// or when no save fn is available, we keep the typed name as free text with entityId null.
// Returns { ok, patch } so the caller can apply the patch and surface an error.
export async function saveAndResolveContactLink(saveContact, contact) {
  const name = (contact?.name || "").trim();
  const fail = { ok: false, patch: { contactName: name, contactEntityId: null } };
  if (typeof saveContact !== "function") return fail;
  try {
    await saveContact(contact);
    return { ok: true, patch: { contactName: name, contactEntityId: contact?.id ?? null } };
  } catch (e) {
    return { ...fail, error: e };
  }
}

/* ═══ CLIENT LINKING ═══ */
// Mirror of saveAndResolveContactLink for the Client field: the link
// (companyEntityId) is set ONLY when the save succeeds, so the quote never holds
// a dangling client id. On failure the typed name stays as free text.
export async function saveAndResolveClientLink(saveClient, client) {
  const name = (client?.name || "").trim();
  const fail = { ok: false, patch: { clientName: name, companyEntityId: null } };
  if (typeof saveClient !== "function") return fail;
  try {
    await saveClient(client);
    return { ok: true, patch: { clientName: name, companyEntityId: client?.id ?? null } };
  } catch (e) {
    return { ...fail, error: e };
  }
}

// Per-client job history for the picker rows ("2 jobs · last Aug 21") — the
// repeat-client signal, computed once per jobs change, not per keystroke.
// Soft-deleted jobs don't count.
export function clientJobStats(jobs) {
  const stats = new Map();
  for (const j of jobs || []) {
    if (!j?.clientId || j.deletedAt) continue;
    const cur = stats.get(j.clientId) || { count: 0, last: "" };
    cur.count += 1;
    const range = getJobDateRange(j);
    const d = range?.end || range?.start || j.captureDate || "";
    if (typeof d === "string" && d > cur.last) cur.last = d;
    stats.set(j.clientId, cur);
  }
  return stats;
}

// One-level "associated studio" family for the picker's history line. A child's
// family = itself + its studio + siblings under that studio; a studio's family =
// itself + its children. Counts sum per-client job totals, and since every job
// has exactly one clientId, the sum IS a unique-job count — no double-counting.
// Informational only: family history must never auto-select the studio as payer
// (Codex design review, 2026-08-26).
export function familyJobStats(client, clients, stats) {
  const own = stats?.get?.(client?.id)?.count || 0;
  if (!client) return { own, family: own };
  const studioId = client.parentClientId || (
    (clients || []).some((c) => c.parentClientId === client.id) ? client.id : null
  );
  if (!studioId) return { own, family: own };
  const memberIds = new Set([studioId, ...(clients || []).filter((c) => c.parentClientId === studioId).map((c) => c.id)]);
  memberIds.add(client.id);
  let family = 0;
  for (const id of memberIds) family += stats?.get?.(id)?.count || 0;
  return { own, family };
}

// Suggest-don't-fill (Ivin, 2026-08-26): when the linked contact's email is on a
// real org domain that matches a known client, offer the link — never write it.
// Free mail hosts (gmail, icloud…) suggest nothing: a shared host is not an
// employer. Returns the client record or null.
export function suggestClientForContact(contact, clients) {
  if (!contact) return null;
  for (const e of recordEmails(contact)) {
    const d = emailDomain(e);
    if (!isOrgDomain(d)) continue;
    const hit = resolveClient({ domain: d }, clients);
    if (hit.record) return hit.record;
  }
  return null;
}

/* ═══ STYLE HELPERS ═══ */
export const hlInp = { ...inp, borderColor: `${C.amber}60`, background: `${C.amber}06` };
export const dividerStyle = { height: 1, background: C.border, margin: "14px 0" };
export const confColor = (c) => c === "high" ? C.green : c === "medium" ? C.amber : C.danger;

/* ═══ DISCOUNT MATH ═══ */
// GUARDRAILS (money audit #1, 2026-06-13): every discount value is coerced with
// Number() (a missing/garbage value becomes 0, never NaN), and every discount is
// clamped so it can be neither negative (a discount must not ADD money — that's a
// markup, not a discount) nor larger than the thing it discounts. This is the one
// chokepoint all quote math flows through (manual entry, AI-refine, imported docs),
// so a negative or NaN total cannot reach job revenue from ANY path. Backstop:
// firestore.rules is off for quotes, so this frontend math is the only guard.
export function calcLineDiscount(li) {
  if (!li || !li.discount) return 0;
  const qty = li.qty > 0 ? Number(li.qty) : 0;
  const rowGross = qty * (Number(li.rate) || 0);
  const value = Number(li.discount.value) || 0;
  const raw = li.discount.type === "%" ? rowGross * (value / 100) : value * qty;
  return Math.min(Math.max(0, raw), rowGross); // 0 ≤ line discount ≤ the line itself
}
export function calcQuoteDiscount(qd, subtotal) {
  if (!qd) return 0;
  const sub = Number(subtotal) || 0;
  const value = Number(qd.value) || 0;
  const raw = qd.type === "%" ? sub * (value / 100) : value;
  return Math.min(Math.max(0, raw), sub); // 0 ≤ one quote discount ≤ the subtotal
}
// `subtotal` is NET of per-line discounts (quote-level % discounts apply to it);
// `grossSubtotal` is the pre-discount list value. `totalSavings` = line discounts +
// quote discounts, so the summary reads Gross → Discounts → Total (Ivin 2026-08-19:
// per-line discounts used to vanish from the Discounts row).
export function calcTotals(lineItems, quoteDiscounts) {
  let grossSubtotal = 0;
  let lineDiscountTotal = 0;
  const subtotal = (lineItems || []).reduce((s, li) => {
    const qty = li.qty != null && li.qty > 0 ? Number(li.qty) : 0;
    const rowGross = qty * (Number(li.rate) || 0);
    // calcLineDiscount already clamps to [0, rowGross]; re-clamp here so a bad rate
    // or discount can never push the user-visible savings negative or non-finite.
    const lineDisc = Math.max(0, Math.min(Number(calcLineDiscount(li)) || 0, Math.max(0, rowGross)));
    grossSubtotal += Math.max(0, rowGross);
    lineDiscountTotal += lineDisc;
    return s + Math.max(0, rowGross - lineDisc); // a line never nets negative
  }, 0);
  // Stacked quote discounts can each be valid yet sum past the subtotal; cap the
  // SUM so the client can never owe a negative amount.
  const rawQuoteDiscount = (quoteDiscounts || []).reduce((s, qd) => s + calcQuoteDiscount(qd, subtotal), 0);
  const quoteDiscountTotal = Math.min(Math.max(0, rawQuoteDiscount), subtotal);
  const total = subtotal - quoteDiscountTotal; // ≥ 0 by construction
  const safe = (n) => (Number.isFinite(n) ? n : 0);
  return {
    grossSubtotal: safe(grossSubtotal),
    subtotal: safe(subtotal),
    lineDiscountTotal: safe(lineDiscountTotal),
    quoteDiscountTotal: safe(quoteDiscountTotal),
    totalSavings: safe(lineDiscountTotal + quoteDiscountTotal),
    total: Math.round(safe(total) * 100) / 100,
  };
}

// Pro bono (2026-09-11): the value a job gave away, for the job card's "Value
// provided" figure and the Insights "Pro bono value" stat. It is the subtotal
// the Pro bono discount cancels: line rates net of any per-line discount, before
// any quote-level discount. Line rates are never zeroed, so this stays readable
// on a $0 job.
export const valueProvided = (lineItems) => calcTotals(lineItems, []).subtotal;

// Whether a document PRINTS as pro bono: the flag is on AND the money really
// reads $0. With the flag on but the Pro bono discount deleted or edited, the
// builder shows its mismatch line and the PDF keeps its payment sentence and
// skips the note, so the client never sees "pro bono" next to a positive total
// (GO review 2026-09-11).
export const proBonoPrints = (q) => isProBono(q) && calcTotals(q?.lineItems, q?.quoteDiscounts).total === 0;

// The discount amounts as shown (PDF totals block, builder rows), in whole cents, so
// the printed Subtotal, discounts and Total add up (2026-10-09: a % discount can land
// on fractions of a cent, e.g. 12.33% of $5,050 is $622.665, and rounding it alone
// printed a cent more than the total moved). What is shared out is exactly what the
// discounts take off as printed: Subtotal minus Total. Quote discounts come first, in
// order, each its own amount to the cent (a $500 discount prints $500), capped by what
// is left, so stacked discounts that calcTotals caps at the subtotal (a discount plus
// Pro bono, two 60%s) never print more than the subtotal. The line-discount total
// shows what remains, so it takes any rounding cent; with no line discounts the last
// quote discount takes it. Display only: calcTotals and every stored total unchanged.
export function shownDiscountAmounts(lineItems, quoteDiscounts) {
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const list = quoteDiscounts || [];
  const totals = calcTotals(lineItems, list);
  const room = Math.max(0, r2(r2(totals.grossSubtotal) - totals.total));
  const hasLine = totals.lineDiscountTotal > 0;
  // The quote discounts' share: what calcTotals took off for them (already capped at
  // the subtotal), to the cent, never more than the whole room.
  let left = Math.max(0, Math.min(room, r2(totals.quoteDiscountTotal)));
  const quote = list.map((qd) => {
    const a = Math.min(r2(calcQuoteDiscount(qd, totals.subtotal)), left);
    left = r2(left - a);
    return a;
  });
  const quoteSum = r2(quote.reduce((s, a) => s + a, 0));
  if (hasLine) return { line: Math.max(0, r2(room - quoteSum)), quote };
  // No line discounts: a rounding remainder (at most a cent per discount) goes on the
  // last non-zero quote discount.
  left = r2(room - quoteSum);
  if (quote.length && left > 0 && left <= 0.01 * quote.length) {
    let i = quote.length - 1;
    while (i > 0 && quote[i] === 0) i -= 1;
    quote[i] = r2(quote[i] + left);
  }
  return { line: 0, quote };
}
/** One quote discount's row on the printed totals. */
export const quoteDiscountRow = (d, amount) => `${d.name || "Quote Discount"}${d.type === "%" ? ` (${d.value}%)` : ""}: ${fmtMoney(-amount)}`;

export const quoteDiscountShownAmounts = (lineItems, quoteDiscounts) => shownDiscountAmounts(lineItems, quoteDiscounts).quote;

// Revenue to book onto a job from a quote (money audit #2, finding B4). The line
// items are the source of truth, so recompute rather than trust a possibly-stale or
// MISSING cached `quote.total` — a migrated/imported quote can lack it, and booking
// it would silently record $0 revenue. Always a finite number ≥ 0; the caller
// warns the operator when it lands at 0.
//
// The $0 boundary (JM15, tightened per gpt F5): a computed 0 is authoritative
// only when EVERY money-bearing line is computable — some row has qty > 0 AND
// no priced row is quantity-less. A genuine zero-out (zero rates, 100%
// discounts) then can't resurrect a stale cached total, while a legacy
// flat-rate row ({qty:0, rate:5000}) — which calcTotals values at 0 by shape,
// not by evidence — still lets a valid cached total win, even when it sits
// next to modern rows.
export function resolveQuoteRevenue(quote) {
  const q = quote || {};
  const items = q.lineItems || [];
  const computed = calcTotals(items, q.quoteDiscounts || []).total;
  if (computed > 0) return computed;
  const hasComputableRow = items.some((li) => li && li.qty != null && li.qty > 0);
  const hasAmbiguousPricedRow = items.some((li) => li && !(li.qty != null && li.qty > 0) && (Number(li.rate) || 0) > 0);
  if (hasComputableRow && !hasAmbiguousPricedRow) return 0;
  const cached = Number(q.total);
  return Number.isFinite(cached) && cached > 0 ? cached : 0;
}

/* ═══ VOLUME PRICING SHEET (F5) ═══ */
// Tiered catalog ids whose multi-quantity presence auto-adds the pricing sheet.
export const TIERED_SCAN_IDS = ["head_scan", "body_scan"];
// True when the quote carries a tiered scan at qty > 1 — the multi-quantity case
// where the volume tier-rate page is relevant. Drives the auto behavior.
export const qualifiesForVolumePricing = (lineItems) =>
  (lineItems || []).some(it => TIERED_SCAN_IDS.includes(it.catalogId) && (Number(it.qty) || 0) > 1);

// Single source of truth for the volume pricing sheet, shared by the builder
// checkbox (QuoteBuilder) and the printed document (QuotePrintable) so they can
// never disagree. `includePricingSheet` is tri-state:
//   true  → operator forced it on  (renders if any tiered item exists)
//   false → operator forced it off (never renders)
//   null/undefined → auto: render only when a multi-quantity tiered scan exists
// Returns:
//   checked    → the builder checkbox state (reflects operator intent / auto)
//   show       → whether the printed PDF includes the page
//   catalogIds → which tier tables to print
//   isAuto     → no explicit override set
//   qualifies  → a multi-qty tiered scan is present
export function resolveVolumePricing(q) {
  const items = q?.lineItems || [];
  const multiQtyIds = items
    .filter(it => TIERED_SCAN_IDS.includes(it.catalogId) && (Number(it.qty) || 0) > 1)
    .map(it => it.catalogId);
  const hasAnyTiered = items.some(it => TIERED_SCAN_IDS.includes(it.catalogId));
  const explicit = q?.includePricingSheet;
  const isAuto = explicit == null;
  const qualifies = multiQtyIds.length > 0;
  const checked = explicit === true ? true : explicit === false ? false : qualifies;
  const show = explicit === false ? false : explicit === true ? hasAnyTiered : qualifies;
  // Always list both tiered scan tables (head + body) whenever the sheet shows —
  // simpler for clients, and avoids dropping a tier when one scan line has qty ≤ 1
  // or is a relabeled custom line. (multiQtyIds still drives `qualifies`/`show`.)
  const catalogIds = [...TIERED_SCAN_IDS];
  return { checked, show, catalogIds, isAuto, qualifies };
}

/* ═══ LIDAR PRICING (RTC 360) ═══ */
// Pricing data lives in LIDAR_MODEL (constants.js); this is the pure calculator that
// turns operator inputs into quote line items. Every price is a fixed rate baked into
// `rate`; the only qty that is not 1 is the DAY COUNT on the per-day lines (capture and
// overtime), so calcTotals (qty×rate) multiplies exactly the lines that are per day.
// Each item is SPECIFIC and TRACEABLE: a plain-English `notes` line (client-facing) plus
// a structured `lidar` params block so any price can be reproduced/audited ("why is this $X").

// Setup count → tier bucket. Actual count is captured (not just the tier) so the line is
// specific and deferred processing stays unambiguous later.
export function lidarTierForSetupCount(count) {
  const n = Number(count) || 0;
  if (n <= 25) return "<=25";
  if (n <= 60) return "26-60";
  if (n <= 99) return "61-99";
  return "100+";
}

// Every line the v2 calculator emits carries lidar.model = LIDAR_MODEL_VERSION. The
// rate-sheet addendum keys on it: a quote whose LiDAR lines predate the stamp was priced
// on the v1 tier matrix and keeps printing that sheet, so a re-exported PDF never shows
// rates that disagree with the priced line on it (Ivin, 2026-09-03).
export const LIDAR_MODEL_VERSION = "v2";

// "v2" when any LiDAR line on the quote was priced by the v2 model, "v1" when the quote
// carries LiDAR lines but none are stamped (archived tier-matrix quotes), null when no
// LiDAR line exists. Custom-id lines never count.
export const lidarModelVersion = (lineItems) => {
  const lidar = (Array.isArray(lineItems) ? lineItems : []).filter(it => isLidarCatalogId(it?.catalogId));
  if (!lidar.length) return null;
  // A quote that mixes archived v1 lines with a new v2 line prints the v2 sheet: the old
  // line's price stands as agreed and its notes still say how it was built (Ivin, 2026-09-03).
  return lidar.some(it => it?.lidar?.model === LIDAR_MODEL_VERSION) ? LIDAR_MODEL_VERSION : "v1";
};

// form = { mode:'captureOnly'|'processing'|'processingOnly', photogrammetry:bool,
//          timeBand:'partial'|'full', dayType:'film'|'standard', crewSize:number,
//          setupCount:number, highDensity:bool, overtimeHrs:number, rush:bool,
//          processingDays:number, days:number }
// Modes: capture + processing (default), capture only (nothing delivered), processing only
// (data already captured — a deferred-processing change order like Apple/Parallax 2026-08).
// Returns { items:[lineItem,...] } or { error:string }.
export function computeLidarLineItems(form) {
  const f = form || {};
  const M = LIDAR_MODEL;
  const model = LIDAR_MODEL_VERSION;
  const mode = f.mode === "captureOnly" ? "captureOnly" : f.mode === "processingOnly" ? "processingOnly" : "processing";
  const hasCapture = mode !== "processingOnly";
  const hasProcessing = mode !== "captureOnly";
  const photogrammetry = !!f.photogrammetry;
  const highDensity = !!f.highDensity;
  const rush = !!f.rush && hasProcessing;
  const texture = photogrammetry ? "photo" : "lidar";

  // Capture days: the per-day lines (capture, overtime) carry it as qty; setups are a job
  // total so processing never multiplies by it.
  const dRaw = Math.round(Number(f.days));
  const days = Number.isFinite(dRaw) && dRaw > 0 ? Math.min(30, dRaw) : 1;

  // Crew is explicit now. Default preserves the old boolean's meaning (photogrammetry
  // implied a second shooter) so a form that predates the field still prices sanely.
  const crewRaw = Math.round(Number(f.crewSize));
  const crewSize = Number.isFinite(crewRaw) && crewRaw > 0 ? Math.min(12, crewRaw) : (photogrammetry ? 2 : 1);

  // Day band: 10 hrs for film/TV, 8 everywhere else, or the short 0-3 hr partial visit.
  const dayType = f.dayType === "film" ? "film" : "standard";
  const timeBand = f.timeBand === "partial" ? "partial" : "full";
  const dayHours = timeBand === "partial" ? M.capture.partialHours : M.capture.dayHours[dayType];

  // Coerce to FINITE non-negative numbers — a money form must never let Infinity / NaN
  // (from a huge or malformed input) flow into a rate.
  const otRaw = Number(f.overtimeHrs);
  const overtimeHrs = Number.isFinite(otRaw) ? Math.min(24, Math.max(0, otRaw)) : 0;
  const scRaw = Math.round(Number(f.setupCount));
  const setupCount = Number.isFinite(scRaw) ? Math.min(100000, Math.max(0, scRaw)) : 0;

  // Rush forces the turnaround it sells, so the estimator cannot promise a compressed
  // delivery without pricing it.
  const pdRaw = Math.round(Number(f.processingDays));
  const processingDays = hasProcessing
    ? (rush ? M.rush.turnaroundDays
            : (Number.isFinite(pdRaw) && pdRaw > 0 ? Math.min(365, pdRaw) : M.processing.defaultDays))
    : null;

  // Block label (2026-09-03): a brief can carry several LiDAR scopes, each priced on its
  // own. The label prefixes every line's description ("Pile: LiDAR capture (RTC 360)"),
  // becomes the line's group (PDF heading + subtotal), and is stamped on the audit block
  // so the builder can tell which brief block is already on the quote. Empty for a single
  // unlabeled scope, which then prints exactly as it always has.
  const label = cleanGroupLabel(f.label);
  const named = (text) => (label ? `${label}: ${text}` : text);
  const groupOf = label ? { group: label } : {};
  const stamp = label ? { block: label } : {};

  const items = [];

  /* ── Capture: base day + each additional head, × days ─────────────────── */
  const baseDay = timeBand === "partial" ? M.capture.partialDay : M.capture.baseDay;
  const captureRate = Math.round(baseDay + (crewSize - 1) * M.capture.perAdditionalCrew);
  if (hasCapture) {
    if (!(captureRate > 0)) return { error: "Could not compute a valid LiDAR capture rate." };

    const captureParts = [
      timeBand === "partial" ? `Partial day (0-${M.capture.partialHours}h)` : `${dayHours}-hr day`,
      `${crewSize} crew`,
    ];
    if (photogrammetry) captureParts.push("+ photogrammetry");
    if (days > 1) captureParts.push(`${days} days`);

    items.push({
      catalogId: "lidar_estimate",
      description: named("LiDAR capture (RTC 360)"),
      notes: captureParts.join(" · "),
      qty: days,
      rate: captureRate,
      discount: null,
      ...groupOf,
      // Structured, reproducible derivation — stored on the line for audit ("why is this $X").
      lidar: {
        ...stamp, model, kind: "capture", mode, timeBand, dayType, dayHours, crewSize, days,
        photogrammetry, texture: photogrammetry ? "r5" : "hdr",
        baseDay, perAdditionalCrew: M.capture.perAdditionalCrew,
        rate: captureRate, processingDays,
      },
    });
  }

  /* ── Processing: linear per setup, with a floor ───────────────────────── */
  let processingRate = 0;
  if (hasProcessing) {
    if (setupCount <= 0) return { error: "Enter a setup count to price processing." };
    const perSetup = M.processing.perSetup[texture];
    const floor = M.processing.minimum[texture];
    const hdMult = highDensity ? M.highDensity.processingMultiplier : 1;
    processingRate = Math.round(Math.max(floor, perSetup * setupCount) * hdMult);

    const grace = M.processing.graceSetups;
    const includedUpTo = setupCount + grace;
    const procParts = [`${setupCount} scan position${setupCount === 1 ? "" : "s"}`];
    if (grace > 0) procParts.push(`up to ${includedUpTo} included`);
    if (highDensity) procParts.push(`high-density ×${hdMult}`);

    items.push({
      catalogId: "lidar_estimate",
      description: named("LiDAR processing"),
      notes: procParts.join(" · "),
      qty: 1,
      rate: processingRate,
      discount: null,
      ...groupOf,
      lidar: {
        ...stamp, model, kind: "processing", mode, setupCount, includedUpTo, perSetup, floor,
        photogrammetry, texture: photogrammetry ? "r5" : "hdr",
        highDensity, highDensityMultiplier: hdMult, rate: processingRate, processingDays,
      },
    });
  }

  /* ── Rush: 50% of processing, floored ─────────────────────────────────── */
  if (rush && processingRate > 0) {
    const rushRate = Math.round(Math.max(M.rush.minimum, processingRate * M.rush.processingMultiplier));
    items.push({
      catalogId: "lidar_estimate",
      description: named("Rush processing"),
      notes: `Processing begins immediately${mode === "processingOnly" ? "" : " after capture"} · ${processingDays} business day turnaround`,
      qty: 1,
      rate: rushRate,
      discount: null,
      ...groupOf,
      lidar: {
        ...stamp, model, kind: "rush", basis: processingRate, multiplier: M.rush.processingMultiplier,
        minimum: M.rush.minimum, rate: rushRate, processingDays,
      },
    });
  }

  /* ── Overtime: past the day length, billed per head, per day ──────────── */
  // A 0-3 hr partial day can never reach overtime (guarding this stops a partial+OT
  // quote that undercuts a full day). Processing-only has no site day to run over.
  if (hasCapture && overtimeHrs > 0 && timeBand === "full") {
    const otRate = M.capture.overtimePerCrewHour * crewSize;
    const otLineRate = Math.round(overtimeHrs * otRate);
    // A sub-dollar sliver of overtime rounds to $0 — drop the line rather than print "$0".
    if (otLineRate > 0) {
      items.push({
        catalogId: "lidar_estimate",
        description: named("LiDAR overtime"),
        notes: `${overtimeHrs} hr past ${dayHours} · ${crewSize} crew × $${M.capture.overtimePerCrewHour}/hr${days > 1 ? " · per day" : ""}`,
        qty: days,
        rate: otLineRate,
        discount: null,
        ...groupOf,
        lidar: { ...stamp, model, kind: "overtime", overtimeHrs, dayHours, crewSize, days, otRate, rate: otLineRate },
      });
    }
  }

  return { items };
}

/* ── LiDAR prefill (AI → estimator) ────────────────────────────────────────
   The AI may never price LiDAR, but it may read FACTS out of the brief (setups,
   crew, hours, rush) so the estimator opens pre-filled. This is the single
   sanitizer for that object: it accepts the model's snake_case shape and the
   stored camelCase shape, keeps ONLY fields that were actually stated (null
   means "not in the brief"), coerces/clamps every value, and derives the time
   band from hours on site. Overtime is derived from hours on site ONLY when the
   day type is stated too: with the day type unknown the day length is unknown,
   and deriving it from context (the quote's industry) made the output depend on
   who called it, so a draft save could re-derive a different overtime than the
   extraction did (GO review, 2026-09-03). The estimator, which knows the day
   type it is showing, derives overtime from hoursOnSite itself. Pure and
   idempotent: normalize(normalize(x)) === normalize(x). Returns null when
   nothing usable was stated so callers can treat "no prefill" uniformly. */
export const LIDAR_PREFILL_KEYS = [
  "label", "note",
  "mode", "dayType", "crewSize", "photogrammetry", "highDensity",
  "timeBand", "hoursOnSite", "overtimeHrs", "setupCount", "rush", "days", "evidence",
];

// A brief can carry this many LiDAR blocks (scopes priced on their own). The 9/12 job
// that motivated blocks had four; twelve leaves room without letting a runaway model
// reply flood the builder with chips.
export const MAX_LIDAR_BLOCKS = 12;

// Evidence keys the model (snake_case) or the stored object (camelCase) may carry, mapped
// to the prefill field they justify. Anything else is dropped.
const LIDAR_EVIDENCE_KEYS = {
  mode: "mode", day_type: "dayType", dayType: "dayType", crew_size: "crewSize", crewSize: "crewSize",
  photogrammetry: "photogrammetry", high_density: "highDensity", highDensity: "highDensity",
  hours_on_site: "hoursOnSite", hoursOnSite: "hoursOnSite", overtime_hrs: "overtimeHrs", overtimeHrs: "overtimeHrs",
  setups: "setupCount", setup_count: "setupCount", setupCount: "setupCount", rush: "rush",
  days: "days", capture_days: "days", time_band: "timeBand", timeBand: "timeBand",
};

// Overtime implied by a stated number of hours on site for a given day type: hours past
// the day length, rounded to the half hour, never negative. 0 when the day type is unknown.
export function lidarOvertimeFromHours(hoursOnSite, dayType) {
  const h = Number(hoursOnSite);
  const dayHours = LIDAR_MODEL.capture.dayHours[dayType];
  if (!Number.isFinite(h) || !dayHours) return 0;
  return Math.max(0, Math.round((h - dayHours) * 2) / 2);
}

export function normalizeLidarPrefill(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const M = LIDAR_MODEL;
  const pick = (...keys) => { for (const k of keys) if (raw[k] !== undefined) return raw[k]; return undefined; };
  const bool = (v) => {
    if (v === true || v === false) return v;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "true" || s === "yes") return true;
      if (s === "false" || s === "no") return false;
    }
    return null;
  };
  const num = (v, min, max, round) => {
    if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    const c = Math.min(max, Math.max(min, n));
    return round ? Math.round(c) : c;
  };
  const out = {};

  const modeRaw = String(pick("mode") ?? "").trim().toLowerCase();
  if (modeRaw === "processing" || modeRaw === "capture_processing" || modeRaw === "capture+processing") out.mode = "processing";
  else if (modeRaw === "captureonly" || modeRaw === "capture_only" || modeRaw === "capture") out.mode = "captureOnly";
  else if (modeRaw === "processingonly" || modeRaw === "processing_only" || modeRaw === "deferred" || modeRaw === "deferred_processing") out.mode = "processingOnly";

  const dtRaw = String(pick("day_type", "dayType") ?? "").trim().toLowerCase();
  if (dtRaw === "film" || dtRaw === "film_tv" || dtRaw === "filmtv") out.dayType = "film";
  else if (dtRaw === "standard") out.dayType = "standard";

  const crew = num(pick("crew_size", "crewSize"), 1, 12, true);
  if (crew != null && crew > 0) out.crewSize = crew;

  const photo = bool(pick("photogrammetry"));
  if (photo != null) out.photogrammetry = photo;
  const hd = bool(pick("high_density", "highDensity"));
  if (hd != null) out.highDensity = hd;
  const rush = bool(pick("rush"));
  if (rush != null) out.rush = rush;

  const setups = num(pick("setups", "setup_count", "setupCount"), 0, 100000, true);
  if (setups != null && setups > 0) out.setupCount = setups;

  const days = num(pick("days", "capture_days"), 0, 30, true);
  if (days != null && days >= 1) out.days = days;

  const tbRaw = String(pick("time_band", "timeBand") ?? "").trim().toLowerCase();
  if (tbRaw === "partial" || tbRaw === "full") out.timeBand = tbRaw;

  const hours = num(pick("hours_on_site", "hoursOnSite"), 0, 24, false);
  if (hours != null && hours > 0) out.hoursOnSite = hours;
  const ot = num(pick("overtime_hrs", "overtimeHrs"), 0, 24, false);
  if (ot != null) out.overtimeHrs = ot;

  // Hours on site are authoritative for the band: a stated 2 hours is a partial day even
  // if a stray time_band says otherwise (GO review, 2026-09-03). Overtime follows only
  // when the day type is stated too (see lidarOvertimeFromHours); otherwise the estimator
  // derives it for the day type it displays, and re-derives it when Standard/Film flips.
  if (out.hoursOnSite != null) {
    out.timeBand = out.hoursOnSite <= M.capture.partialHours ? "partial" : "full";
    if (out.overtimeHrs == null && out.timeBand === "full" && out.dayType) {
      out.overtimeHrs = lidarOvertimeFromHours(out.hoursOnSite, out.dayType);
    }
  }

  // Evidence: the phrase the model read each fact from, kept only for facts that made it
  // into `out`, trimmed, em-dash-stripped, capped. Shown on the estimator's "brief" tags
  // so a guessed number is obvious at a glance.
  const evRaw = pick("evidence", "lidar_evidence");
  if (evRaw && typeof evRaw === "object" && !Array.isArray(evRaw)) {
    const ev = {};
    for (const [k, v] of Object.entries(evRaw)) {
      const field = LIDAR_EVIDENCE_KEYS[k];
      if (!field || !(field in out) || typeof v !== "string") continue;
      const text = stripEmDashes(v).replace(/\s+/g, " ").trim().slice(0, 160);
      if (text) ev[field] = text;
    }
    if (Object.keys(ev).length) out.evidence = ev;
  }

  // Block identity (2026-09-03): a short label naming the scope ("Pile", "Fresh Kills
  // tent") and an optional heading note (a date or place line). Both are client-visible:
  // the label prefixes every line the block inserts and names its group on the PDF, the
  // note prints under the group heading. Kept last so a label-only block still counts as
  // a block (a named scope the estimator opens on its defaults).
  const label = cleanGroupLabel(pick("label", "block", "name"));
  if (label) out.label = label;
  const note = cleanGroupNote(pick("note", "heading_note", "headingNote"));
  if (note) out.note = note;

  return Object.keys(out).length ? out : null;
}

/* One brief can carry several LiDAR scopes priced on their own (2026-09-03: the 9/12
   job had four, on three sites). The stored prefill is a LIST of blocks, each the
   normalizeLidarPrefill() shape plus its label. Accepts the model's array, a single
   object (single-scope briefs and every quote saved before blocks existed), or
   { blocks: [...] }. Empty entries are dropped, the list is capped, and null comes back
   when nothing usable was stated so callers treat "no prefill" uniformly. Idempotent. */
export function normalizeLidarBlocks(raw) {
  if (!raw || typeof raw !== "object") return null;
  const list = Array.isArray(raw) ? raw : (Array.isArray(raw.blocks) ? raw.blocks : [raw]);
  const blocks = list.map((b) => normalizeLidarPrefill(b)).filter(Boolean).slice(0, MAX_LIDAR_BLOCKS);
  if (blocks.length > 1) {
    // A label is a block's identity (its chip, whether it is inserted, the line prefix, the
    // group), so with several blocks every one needs a distinct, non-empty label: an
    // unlabeled block becomes "Block N", a repeat becomes "Pile (2)" (GO review, 2026-09-03).
    // A single block may stay unlabeled and prints exactly as it always has.
    const seen = new Set();
    blocks.forEach((b, i) => {
      const root = (cleanGroupLabel(b.label) || `Block ${i + 1}`).slice(0, MAX_GROUP_LABEL - 5).trim();
      let label = root;
      for (let n = 2; seen.has(label); n++) label = `${root} (${n})`;
      seen.add(label);
      b.label = label;
    });
  }
  return blocks.length ? blocks : null;
}

// What a block is called in chips, banners and line text when the brief gave no label.
export const lidarBlockName = (block, i) => cleanGroupLabel(block?.label) || `Block ${(Number(i) || 0) + 1}`;

// A block is "inserted" once the quote carries a LiDAR line stamped with its label. Labels
// are the identity (there is no block id), so two blocks with the same label are one. An
// unlabeled block stamps nothing, so it counts as inserted once any unstamped LiDAR line
// exists (the single-scope case, and every quote that predates blocks).
export const lidarBlockInserted = (block, i, lineItems) => {
  const label = cleanGroupLabel(block?.label);
  const lidarLines = (lineItems || []).filter((li) => isLidarCatalogId(li?.catalogId));
  if (!label) return lidarLines.some((li) => !cleanGroupLabel(li?.lidar?.block));
  return lidarLines.some((li) => cleanGroupLabel(li?.lidar?.block) === label);
};

// Blocks from the prefill that have no LiDAR line on the quote yet, with their index.
export const pendingLidarBlocks = (prefill, lineItems) =>
  (normalizeLidarBlocks(prefill) || []).map((block, i) => ({ block, i })).filter(({ block, i }) => !lidarBlockInserted(block, i, lineItems));

// The estimator's opening state: every stated fact from the prefill, then the documented
// defaults. ONE definition, used by LidarEstimator on mount and by the scenario tests, so
// the tests cannot drift from what the widget actually does (GO review, 2026-09-03).
//   crew: 2 when photogrammetry is on (a second shooter), else 1
//   day type: film (10 hr) for film/TV quotes, else standard (8 hr)
//   overtime: stated, else implied by stated hours on site for that day type, else 0
//   setups 25 · full day · 5-day turnaround · rush off · 1 day
export function lidarEstimatorDefaults(prefill, industry) {
  // One block, in any shape the normalizer accepts: a block, a list (its first block, for a
  // caller that predates blocks), or a { blocks } wrapper (GO review, 2026-09-03).
  const p = normalizeLidarBlocks(prefill)?.[0] || {};
  const photogrammetry = p.photogrammetry ?? false;
  const dayType = p.dayType ?? (industry === "film_tv" ? "film" : "standard");
  const impliedOT = (p.hoursOnSite != null && p.overtimeHrs == null) ? lidarOvertimeFromHours(p.hoursOnSite, dayType) : null;
  return {
    label: p.label ?? "",
    note: p.note ?? "",
    mode: p.mode ?? "processing",
    dayType,
    crewSize: p.crewSize ?? (photogrammetry ? 2 : 1),
    photogrammetry,
    timeBand: p.timeBand ?? "full",
    setupCount: p.setupCount ?? 25,
    highDensity: p.highDensity ?? false,
    overtimeHrs: p.overtimeHrs ?? impliedOT ?? 0,
    processingDays: LIDAR_MODEL.processing.defaultDays,
    rush: p.rush ?? false,
    days: p.days ?? 1,
  };
}

// True when a catalog id refers to LiDAR (priced/managed ONLY by the estimator widget).
// Tolerant of case/whitespace and matches the legacy env_lidar* ids so a drifted or invented
// id can't slip past the guard — a stray "LIDAR_ESTIMATE" or "env_lidar_photo_hd" still counts.
export const isLidarCatalogId = (id) => {
  const s = String(id ?? "").trim().toLowerCase();
  return s === "lidar_estimate" || s.startsWith("env_lidar");
};

// True when a quote carries any LiDAR line — gates the rate-sheet addendum. Mirrors
// qualifiesForVolumePricing. Includes the legacy env_lidar ids so archived quotes count.
export const hasLidarLineItem = (lineItems) =>
  (lineItems || []).some(it => isLidarCatalogId(it.catalogId));

// True when any LiDAR line on the quote was priced with photogrammetry (the Canon R5
// texture pass). Reads the audit block the estimator stamps on v2 and stamped v1 lines,
// plus the legacy env_lidar_photo ids, which carried photogrammetry in the id.
export const lidarUsesPhotogrammetry = (lineItems) =>
  (lineItems || []).some((it) => {
    if (!isLidarCatalogId(it?.catalogId)) return false;
    if (it?.lidar?.photogrammetry === true || it?.lidar?.texture === "r5") return true;
    return String(it?.catalogId ?? "").trim().toLowerCase().startsWith("env_lidar_photo");
  });

// A SHEET_EQUIPMENT card prints only once Ivin has verified it and it has a model, an
// image file and at least one spec row.
export const equipmentCardReady = (card) =>
  !!(card && card.verified === true && String(card.model ?? "").trim() && String(card.image ?? "").trim()
    && card.specs && typeof card.specs === "object" && Object.keys(card.specs).length > 0);

// The cards the v2 LiDAR equipment page prints, in order: the RTC 360 always, the R5
// when photogrammetry is on, then every ready SHEET_EQUIPMENT card the quote uses (a
// line with one of its catalog ids, or one of its equipment ids on the quote). Each
// card is { key, model, type, specs, image }, image being a file name in
// src/assets/equipment/. The archived v1 sheet does not use this: it keeps printing
// what it printed when the quote went out.
export function lidarSheetEquipment(quote, extras = SHEET_EQUIPMENT) {
  const lineItems = Array.isArray(quote?.lineItems) ? quote.lineItems : [];
  const { rtc360, r5 } = LIDAR_MODEL.equipment;
  const cards = [{ key: "rtc360", image: "rtc.jpg", model: rtc360.model, type: rtc360.type, specs: rtc360.specs }];
  if (lidarUsesPhotogrammetry(lineItems)) cards.push({ key: "r5", image: "r5.jpg", model: r5.model, type: r5.type, specs: r5.specs });

  const ready = Object.entries(extras || {}).filter(([, card]) => equipmentCardReady(card));
  if (!ready.length) return cards;
  const catalogIds = new Set(lineItems.map((it) => String(it?.catalogId ?? "").trim().toLowerCase()).filter(Boolean));
  // The quote's equipment list holds ids, or display names on quotes saved before ids
  // (migrateQuote). The holds reader resolves both: a card's equipmentIds, its model as
  // the display name, and the holds engine's legacy names ("PortalCam").
  const nameMap = Object.fromEntries(ready.flatMap(([, card]) => (card.equipmentIds || []).map((id) => [id, card.model])));
  const equipmentIds = new Set(quoteEquipmentIds(quote, nameMap));
  for (const [key, card] of ready) {
    const used = (card.catalogIds || []).some((id) => catalogIds.has(String(id).toLowerCase()))
      || (card.equipmentIds || []).some((id) => equipmentIds.has(id));
    if (used) cards.push({ key, model: card.model, type: card.type || null, specs: card.specs, image: card.image });
  }
  return cards;
}

// Business days to process the LiDAR work on a quote (max across LiDAR processing lines),
// or null if there's no processing line. Drives the deliverables turnaround line + rate-sheet note.
export const lidarProcessingDays = (lineItems) => {
  const d = (lineItems || []).map(it => it?.lidar?.processingDays).filter(n => Number.isFinite(n) && n > 0);
  return d.length ? Math.max(...d) : null;
};

/* ═══ DRONE PRICING (DJI Mavic 3E) ═══ */
// Pricing data lives in DRONE_MODEL (constants.js); this is the pure calculator, the
// sibling of computeLidarLineItems: separate Capture / Processing / Rush / Overtime
// lines, the per-day lines carrying the day count as qty, every rate an integer, and a
// structured `drone` audit block on each line so any price can be reproduced. Approved
// by Ivin 2026-09-04; canonical record docs/pricing/drone-pricing.md.

// True when a catalog id refers to the drone estimator's lines. drone_estimate is NOT in
// RATE_CATALOG (like lidar_estimate), so neither the catalog browser nor the AI can insert
// a $0 drone line. Tolerant of case and whitespace like isLidarCatalogId.
export const isDroneCatalogId = (id) => String(id ?? "").trim().toLowerCase() === "drone_estimate";

// A line priced by either estimator: never the AI's to price, rename, or remove.
export const isEstimatorCatalogId = (id) => isLidarCatalogId(id) || isDroneCatalogId(id);

// True when a quote carries any drone line. Gates the drone rate-sheet addendum.
export const hasDroneLineItem = (lineItems) => (lineItems || []).some((it) => isDroneCatalogId(it?.catalogId));
export const hasEstimatorLineItem = (lineItems) => hasLidarLineItem(lineItems) || hasDroneLineItem(lineItems);

// Business days to process the drone work on a quote (max across drone lines that carry
// processingDays), or null when there is no drone processing line.
export const droneProcessingDays = (lineItems) => {
  const d = (lineItems || []).map((it) => it?.drone?.processingDays).filter((n) => Number.isFinite(n) && n > 0);
  return d.length ? Math.max(...d) : null;
};

// The model that priced a quote's drone lines: the stamp on the FIRST drone line (the
// sheet must follow what priced the line, not today's constant, so a re-exported PDF of a
// quote priced on an earlier model keeps that model's sheet, as lidarModelVersion does for
// the LiDAR v1 freeze). A drone line with no stamp reads as the current model, because
// there was never an unstamped generation. null when no drone line exists.
export const droneModelVersion = (lineItems) => {
  const drone = (Array.isArray(lineItems) ? lineItems : []).filter((it) => isDroneCatalogId(it?.catalogId));
  if (!drone.length) return null;
  return drone[0]?.drone?.model || DRONE_MODEL_VERSION;
};

// Included acreage: the quoted acres plus the grace share, rounded UP to a tenth of an
// acre so the printed ceiling never sits below the true figure (11.5 acres → 12.7).
// Goes through a fixed-point step first so 10 × 1.1 prints 11, not 11.1.
export const droneIncludedAcres = (acres) => {
  const a = Number(acres);
  if (!Number.isFinite(a) || a <= 0) return 0;
  const tenths = Number((a * (1 + DRONE_MODEL.processing.graceShare) * 10).toFixed(6));
  return Math.ceil(tenths) / 10;
};

// Acres print with at most two decimals and no trailing zeros ("11.5", "10", "12.75").
export const fmtAcres = (n) => String(Math.round(Number(n) * 100) / 100);

// v1 carries no AI prefill: the estimator opens on its defaults, optionally under a block
// label and heading note handed in by the builder. Accepts a block, a list (its first
// entry), or nothing. Returns null when nothing usable was given.
export function normalizeDroneBlock(raw) {
  const b = Array.isArray(raw) ? raw[0] : raw;
  if (!b || typeof b !== "object") return null;
  const out = {};
  const label = cleanGroupLabel(b.label ?? b.block ?? b.name);
  if (label) out.label = label;
  const note = cleanGroupNote(b.note ?? b.headingNote ?? b.heading_note);
  if (note) out.note = note;
  return Object.keys(out).length ? out : null;
}

// The estimator's opening state, ONE definition shared with the tests (as
// lidarEstimatorDefaults is): capture + processing, a film day for film/TV quotes else a
// standard day, full day, 1 crew (the pilot), 10 acres, geometry only, the default
// turnaround, rush off, no overtime, 1 day.
export function droneEstimatorDefaults(prefill, industry) {
  const p = normalizeDroneBlock(prefill) || {};
  return {
    label: p.label ?? "",
    note: p.note ?? "",
    mode: "processing",
    dayType: industry === "film_tv" ? "film" : "standard",
    timeBand: "full",
    crewSize: 1,
    acres: 10,
    deliverable: "geometry",
    processingDays: DRONE_MODEL.processing.defaultDays,
    rush: false,
    overtimeHrs: 0,
    days: 1,
  };
}

// form = { mode:'processing'|'captureOnly'|'processingOnly', dayType:'film'|'standard',
//          timeBand:'partial'|'full', crewSize:number, acres:number (decimals allowed),
//          deliverable:'geometry'|'textured', processingDays:number, rush:bool,
//          overtimeHrs:number, days:number, label:string }
// Returns { items:[lineItem,...] } or { error:string }. Money rounds the way
// computeLidarLineItems rounds: Math.round on every line rate, so a half dollar rounds up
// ($402.50 of rush on the 11.5-acre Fresh Kills case prints as $403).
export function computeDroneLineItems(form) {
  const f = form || {};
  const M = DRONE_MODEL;
  const model = DRONE_MODEL_VERSION;
  const mode = f.mode === "captureOnly" ? "captureOnly" : f.mode === "processingOnly" ? "processingOnly" : "processing";
  const hasCapture = mode !== "processingOnly";
  const hasProcessing = mode !== "captureOnly";
  const deliverable = f.deliverable === "textured" ? "textured" : "geometry";
  const rush = !!f.rush && hasProcessing;

  // Capture days: the per-day lines (capture, overtime) carry it as qty; acres are a job
  // total so processing never multiplies by it.
  const dRaw = Math.round(Number(f.days));
  const days = Number.isFinite(dRaw) && dRaw > 0 ? Math.min(30, dRaw) : 1;
  const crewRaw = Math.round(Number(f.crewSize));
  const crewSize = Number.isFinite(crewRaw) && crewRaw > 0 ? Math.min(12, crewRaw) : 1;

  const dayType = f.dayType === "film" ? "film" : "standard";
  const timeBand = f.timeBand === "partial" ? "partial" : "full";
  const dayHours = timeBand === "partial" ? M.capture.partialHours : M.capture.dayHours[dayType];

  // Finite, non-negative numbers only: a money form never lets Infinity or NaN into a
  // rate. Acres accept decimals (11.5), kept to two places and capped.
  const otRaw = Number(f.overtimeHrs);
  const overtimeHrs = Number.isFinite(otRaw) ? Math.min(24, Math.max(0, otRaw)) : 0;
  const acRaw = Number(f.acres);
  const acres = Number.isFinite(acRaw) ? Math.min(100000, Math.max(0, Math.round(acRaw * 100) / 100)) : 0;

  // Rush forces the turnaround it sells.
  const pdRaw = Math.round(Number(f.processingDays));
  const processingDays = hasProcessing
    ? (rush ? M.rush.turnaroundDays
            : (Number.isFinite(pdRaw) && pdRaw > 0 ? Math.min(365, pdRaw) : M.processing.defaultDays))
    : null;

  // Block label: prefixes every line, names the group, and is stamped on the audit block
  // (same contract as the LiDAR estimator). Empty for a single unlabeled scope.
  const label = cleanGroupLabel(f.label);
  const named = (text) => (label ? `${label}: ${text}` : text);
  const groupOf = label ? { group: label } : {};
  const stamp = label ? { block: label } : {};

  const items = [];

  /* ── Capture: base day + each additional head, × days ─────────────────── */
  const baseDay = timeBand === "partial" ? M.capture.partialDay : M.capture.baseDay;
  const captureRate = Math.round(baseDay + (crewSize - 1) * M.capture.perAdditionalCrew);
  if (hasCapture) {
    if (!(captureRate > 0)) return { error: "Could not compute a valid drone capture rate." };
    const captureParts = [
      timeBand === "partial" ? `Partial day (0 to ${M.capture.partialHours} hrs)` : `${dayHours}-hr day`,
      `${crewSize} crew`,
    ];
    if (days > 1) captureParts.push(`${days} days`);
    items.push({
      catalogId: "drone_estimate",
      description: named("Drone capture (Mavic 3E)"),
      notes: captureParts.join(" · "),
      qty: days,
      rate: captureRate,
      discount: null,
      ...groupOf,
      drone: {
        ...stamp, model, kind: "capture", mode, timeBand, dayType, dayHours, crewSize, days,
        baseDay, perAdditionalCrew: M.capture.perAdditionalCrew, rate: captureRate, processingDays,
      },
    });
  }

  /* ── Processing: linear per acre, with a floor ────────────────────────── */
  let processingRate = 0;
  if (hasProcessing) {
    if (!(acres > 0)) return { error: "Enter the acres flown to price processing." };
    const perAcre = M.processing.perAcre[deliverable];
    const floor = M.processing.minimum[deliverable];
    processingRate = Math.round(Math.max(floor, perAcre * acres));
    const includedUpTo = droneIncludedAcres(acres);
    const what = deliverable === "textured" ? "textured mesh" : "geometry only";
    items.push({
      catalogId: "drone_estimate",
      description: named(`Drone processing, ${what} (${fmtAcres(acres)} acre${acres === 1 ? "" : "s"}, up to ${fmtAcres(includedUpTo)} included)`),
      notes: deliverable === "textured"
        ? "Textured mesh from the drone stills, delivered raw"
        : "Scaled point cloud and mesh from the drone stills, no textures, delivered raw",
      qty: 1,
      rate: processingRate,
      discount: null,
      ...groupOf,
      drone: {
        ...stamp, model, kind: "processing", mode, deliverable, acres, includedUpTo,
        graceShare: M.processing.graceShare, perAcre, floor, rate: processingRate, processingDays,
      },
    });
  }

  /* ── Rush: 50% of processing, no minimum ──────────────────────────────── */
  if (rush && processingRate > 0) {
    const rushRate = Math.round(Math.max(M.rush.minimum, processingRate * M.rush.processingMultiplier));
    items.push({
      catalogId: "drone_estimate",
      description: named("Drone rush processing"),
      notes: `Processing begins immediately${mode === "processingOnly" ? "" : " after capture"} · ${processingDays} business day turnaround`,
      qty: 1,
      rate: rushRate,
      discount: null,
      ...groupOf,
      drone: {
        ...stamp, model, kind: "rush", basis: processingRate, multiplier: M.rush.processingMultiplier,
        minimum: M.rush.minimum, rate: rushRate, processingDays,
      },
    });
  }

  /* ── Overtime: past the day length, billed per head, per day ──────────── */
  // A 0 to 3 hr partial day can never reach overtime; processing only has no site day.
  if (hasCapture && overtimeHrs > 0 && timeBand === "full") {
    const otRate = M.capture.overtimePerCrewHour * crewSize;
    const otLineRate = Math.round(overtimeHrs * otRate);
    if (otLineRate > 0) {
      items.push({
        catalogId: "drone_estimate",
        description: named("Drone overtime"),
        notes: `${overtimeHrs} hr past ${dayHours} · ${crewSize} crew${days > 1 ? " · per day" : ""}`,
        qty: days,
        rate: otLineRate,
        discount: null,
        ...groupOf,
        drone: { ...stamp, model, kind: "overtime", overtimeHrs, dayHours, crewSize, days, otRate, rate: otLineRate },
      });
    }
  }

  return { items };
}

/* ═══ LINE GROUPS ═══
   A line item may carry a `group`: a short, client-visible label such as "Pile" or
   "Fresh Kills" (2026-09-03). The builder list and the PDF print grouped lines under a
   heading with a subtotal, in the order each group's first line appears on the quote;
   lines with no group print last, under no heading. A heading note (a date or place
   line) lives on the quote as lineGroupNotes[label]. The estimator stamps its block
   label as the group; the AI copies a group the operator's notes assign to a line; the
   operator can move any line. A quote with no groups renders exactly as it did before. */
export const MAX_GROUP_LABEL = 60;
export const MAX_GROUP_NOTE = 120;
// Trimmed again after the cap so a cut that lands on a space stays idempotent.
export const cleanGroupLabel = (v) => stripEmDashes(String(v ?? "")).replace(/\s+/g, " ").trim().slice(0, MAX_GROUP_LABEL).trim();
export const cleanGroupNote = (v) => stripEmDashes(String(v ?? "")).replace(/\s+/g, " ").trim().slice(0, MAX_GROUP_NOTE).trim();
export const hasLineGroups = (lineItems) => (lineItems || []).some((li) => !!cleanGroupLabel(li?.group));

// Gross line total (qty × rate), the figure the Total column shows and the Subtotal sums;
// discounts print beneath the line and roll into the totals block, so a group subtotal is
// gross as well and the column still adds up.
export const lineGross = (li) => {
  const qty = li?.qty != null && Number(li.qty) > 0 ? Number(li.qty) : 0;
  return qty * (Number(li?.rate) || 0);
};

// The block labels a quote's prefill carries, in brief order: the preferred order of the
// groups on the page, so the sites print in the order the brief listed them no matter
// which line the AI happened to emit first.
export const lidarBlockLabels = (prefill) =>
  (normalizeLidarBlocks(prefill) || []).map((b) => cleanGroupLabel(b.label)).filter(Boolean);

// Ordered groups for rendering: [{ label, note, items: [{ item, idx }], subtotal }]. Groups
// named in `preferredOrder` (the brief's block labels) come first in that order, then the
// rest by first appearance, then the unlabeled remainder with label "". `idx` is the line's
// position in the quote's lineItems, which every edit callback keys on, so a grouped list
// still edits the right line.
export function groupLineItems(lineItems, groupNotes, preferredOrder = []) {
  const items = Array.isArray(lineItems) ? lineItems : [];
  const notes = (groupNotes && typeof groupNotes === "object" && !Array.isArray(groupNotes)) ? groupNotes : {};
  const groups = [];
  const byLabel = new Map();
  let rest = null;
  const present = new Set(items.map((li) => cleanGroupLabel(li?.group)).filter(Boolean));
  (Array.isArray(preferredOrder) ? preferredOrder : []).map(cleanGroupLabel).filter((l) => l && present.has(l)).forEach((label) => {
    if (byLabel.has(label)) return;
    const g = { label, note: cleanGroupNote(notes[label]), items: [], subtotal: 0 };
    byLabel.set(label, g);
    groups.push(g);
  });
  items.forEach((item, idx) => {
    const label = cleanGroupLabel(item?.group);
    let g;
    if (!label) {
      if (!rest) rest = { label: "", note: "", items: [], subtotal: 0 };
      g = rest;
    } else {
      g = byLabel.get(label);
      if (!g) {
        g = { label, note: cleanGroupNote(notes[label]), items: [], subtotal: 0 };
        byLabel.set(label, g);
        groups.push(g);
      }
    }
    g.items.push({ item, idx });
    g.subtotal += lineGross(item);
  });
  // Inside a labeled group the estimator's lines lead (capture, processing, rush, overtime
  // in the order it emitted them) and everything else follows in the order it was added:
  // the AI adds a block's custom lines before the block itself is inserted, and a heading
  // that opens with "Pile photogrammetry reconstruction" above "Pile: LiDAR capture" reads
  // backwards. Stable sort, so nothing else moves. The unlabeled remainder keeps quote order.
  const rank = (li) => (isLidarCatalogId(li?.catalogId) ? 0 : 1);
  groups.forEach((g) => { g.items.sort((a, b) => (rank(a.item) - rank(b.item)) || (a.idx - b.idx)); });
  if (rest) groups.push(rest);
  return groups;
}

// Distinct group labels on a quote, in display order (the builder's group picker).
export const lineGroupLabels = (lineItems, preferredOrder = []) => groupLineItems(lineItems, null, preferredOrder).map((g) => g.label).filter(Boolean);

// The persisted shape of lineGroupNotes: clean labels and notes, nothing empty, no junk.
export function sanitizeGroupNotes(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const label = cleanGroupLabel(k);
    const note = cleanGroupNote(v);
    if (label && note) out[label] = note;
  }
  return out;
}

/* ═══ LINE GROUPS AS BLOCKS (2026-09-04) ═══
   quote.lineGroups is the ordered list of blocks on a quote: [{ label, note }]. It is
   what lets a block exist before it has a line (New block, then fill it) and what fixes
   the order the blocks print in. Every quote saved before this slice has no list, so
   the list is DERIVED from the fields that carried blocks until now: the brief's LiDAR
   block labels in brief order, then every other group on the lines in first-appearance
   order, with notes from lineGroupNotes. That is exactly the order the page showed
   before, so an old quote opens unchanged. Once a list exists it is the source of the
   order and the notes; lineGroupNotes is re-derived from it on every write (the PDF,
   old readers and the version snapshots keep reading lineGroupNotes). A group typed on
   a line that is not in the list is appended, so a typed name creates a block.
   `raw` absent (null/undefined, or junk) means the quote predates the list: derive it.
   `raw` an array, even an empty one, IS the list: a brief label the operator removed
   with the x must not come back from the prefill, so the prefill is never consulted
   once a list exists. Load paths stamp the derived list before BLANK_QUOTE's [] can
   hide an old record behind an empty array. Labels are the identity (cleanGroupLabel),
   exactly as groupLineItems keys them. */
export const MAX_LINE_GROUPS = 30;

export function normalizeLineGroups(raw, { lidarPrefill, lineGroupNotes, lineItems } = {}) {
  const notes = sanitizeGroupNotes(lineGroupNotes);
  const out = [];
  const seen = new Set();
  const push = (label, note, printLabel) => {
    const l = cleanGroupLabel(label);
    if (!l || seen.has(l) || out.length >= MAX_LINE_GROUPS) return;
    seen.add(l);
    // printLabel: what the heading and subtotal print, when it isn't the label. The label
    // stays the block's identity (lines, LiDAR and the estimators key on it).
    const shown = cleanGroupLabel(printLabel);
    out.push({ label: l, note: cleanGroupNote(note === undefined ? notes[l] : note), ...(shown ? { printLabel: shown } : {}) });
  };
  if (Array.isArray(raw)) {
    raw.forEach((g) => {
      if (typeof g === "string") push(g);
      else if (g && typeof g === "object") push(g.label, g.note, g.printLabel);
    });
  } else {
    lidarBlockLabels(lidarPrefill).forEach((l) => push(l));
  }
  // A stored list is the whole list. A block it doesn't name was removed, and saves merge, so
  // the note map can still hold that block's old note. A new block of that name starts clean.
  (Array.isArray(lineItems) ? lineItems : []).forEach((li) => push(li?.group, Array.isArray(raw) ? "" : undefined));
  return out;
}

// The block list of a quote in any state: the persisted list, or the derived one for a
// quote that predates it. The one call every reader and every load path goes through.
export const quoteLineGroups = (q) =>
  normalizeLineGroups(q?.lineGroups, { lidarPrefill: q?.lidarPrefill, lineGroupNotes: q?.lineGroupNotes, lineItems: q?.lineItems });

// The estimator's Insert applied to a quote: appends the block's lines (key-suffixed so a
// base + overtime line created in the same tick stay distinct) and lands the block's
// heading note on its lineGroups entry, created if the label is new. A block's label is
// its identity: an operator who renamed the block before inserting must find it marked
// inserted afterwards, so the prefill block takes its new name, and its lineGroups entry
// and the lines already grouped under it follow; a cleared note is removed, not left over
// (Codex review, 2026-09-03). blockIndex is honored only when the prefill really has that
// block: the estimator opened for a target block reports none, so it can never rename a
// brief block (2026-09-04). Pure; the builder calls it inside setQ.
export function applyLidarInsert(cur, items, meta = {}) {
  const label = cleanGroupLabel(meta.label);
  const note = cleanGroupNote(meta.note);
  const blocks = normalizeLidarBlocks(cur.lidarPrefill);
  const i = meta.blockIndex;
  const oldLabel = (blocks && Number.isInteger(i) && blocks[i]) ? cleanGroupLabel(blocks[i].label) : "";
  const renamed = !!(oldLabel && label && oldLabel !== label);
  let lidarPrefill = cur.lidarPrefill;
  let lineItems = cur.lineItems || [];
  let groups = quoteLineGroups(cur);
  if (renamed) {
    lidarPrefill = blocks.map((b, k) => (k === i ? { ...b, label } : b));
    lineItems = lineItems.map((li) => (cleanGroupLabel(li?.group) === oldLabel ? { ...li, group: label } : li));
    groups = groups.map((g) => (g.label === oldLabel ? { ...g, label } : g));
  }
  if (label) {
    groups = groups.some((g) => g.label === label)
      ? groups.map((g) => (g.label === label ? { ...g, note } : g))
      : [...groups, { label, note }];
  }
  groups = normalizeLineGroups(groups);
  const base = Date.now();
  return {
    ...cur,
    lidarPrefill,
    lineItems: [...lineItems, ...(items || []).map((it, k) => ({ ...it, key: `li_${base}_${k}_${Math.random().toString(36).slice(2, 6)}` }))],
    lineGroups: groups,
    lineGroupNotes: lineGroupNotesFrom(groups),
  };
}

// An estimator Insert with no brief walk behind it (the drone estimator, 2026-09-04):
// the same append + block landing as applyLidarInsert, with blockIndex pinned to null so
// the prefill can never be renamed from this path whatever meta carries. Reuses
// applyLidarInsert rather than copying it, so the two estimators can never land a block
// differently. Pure; the builder calls it inside setQ.
export const applyEstimatorInsert = (cur, items, meta = {}) =>
  applyLidarInsert(cur, items, { label: meta.label, note: meta.note, blockIndex: null });

// The block a typed group name means, case-insensitively: "pile" typed on a line while a
// "Pile" block exists resolves to "Pile", so the line joins that block and no second
// heading appears. A name with no match is the cleaned typed value (a new block).
export function resolveGroupLabel(lineGroups, typed) {
  const label = cleanGroupLabel(typed);
  if (!label) return "";
  const hit = (Array.isArray(lineGroups) ? lineGroups : []).find((g) => cleanGroupLabel(g?.label).toLowerCase() === label.toLowerCase());
  return hit ? cleanGroupLabel(hit.label) : label;
}

// The persisted lineGroupNotes shape ({ label: note }) derived from the block list.
export const lineGroupNotesFrom = (lineGroups) =>
  Object.fromEntries((Array.isArray(lineGroups) ? lineGroups : []).filter((g) => g && cleanGroupLabel(g.label) && cleanGroupNote(g.note)).map((g) => [cleanGroupLabel(g.label), cleanGroupNote(g.note)]));

/* ═══ DEPOSIT ═══ */
// Single reader for the deposit shape. Precedence: a stored `deposit` object
// always wins; the legacy `requiresDeposit` boolean is consulted only when no
// deposit object exists (legacy docs edited under the new model carry both).
// Returns a sanitized {type, value}; anything invalid degrades to "none".
export function getDeposit(q) {
  const d = q?.deposit;
  if (d && typeof d === "object" && d.type) {
    const legacy = !!d.legacy;
    const value = Number(d.value);
    if (d.type === "percent" && Number.isFinite(value) && value > 0 && value <= 100) return { type: "percent", value, legacy };
    if (d.type === "amount" && Number.isFinite(value) && value > 0) return { type: "amount", value, legacy };
    if (d.type === "full_session") return { type: "full_session", value: null, legacy };
    return { type: "none", value: null, legacy };
  }
  if (q?.requiresDeposit) return { type: "full_session", value: null, legacy: true };
  return { type: "none", value: null, legacy: !d };
}

// The generated payment sentence for the Terms block. Legacy quotes (no stored
// `deposit` object) keep their historical sentences verbatim — including
// "before the 3D models are delivered" — so reprinting an old quote does not
// change its terms. New-model quotes get sentences that make no claims about
// models or delivery timing (that lives in the dataDelivery section).
export function buildPaymentSentence(q) {
  const custom = customPaymentSentence(q, depositDollars(q), calcTotals(q?.lineItems, q?.quoteDiscounts).total);
  return custom !== null ? custom : generatedPaymentSentence(q);
}

// What {deposit} fills with in custom payment wording, by the same math as the sentence
// below. null when there is no dollar figure to fill: none and full_session.
export function depositDollars(q) {
  const dep = getDeposit(q);
  if (dep.type === "percent") return Math.round(calcTotals(q?.lineItems, q?.quoteDiscounts).total * dep.value) / 100;
  if (dep.type === "amount") return dep.value;
  return null;
}

// What the builder, the PDF preview and the box warn about in custom payment wording.
// Never printed on the quote.
export function printWordingWarnings(q) {
  const text = wordingOf(q, "paymentSentence");
  if (!text) return [];
  const out = [];
  if (text.includes("{deposit}") && depositDollars(q) === null) {
    out.push("The custom payment wording uses {deposit}, and this deposit has no dollar amount, so the tool's own sentence prints instead. Rewrite or reset that wording.");
  }
  if (q?.printWordingMoneyKey && q.printWordingMoneyKey !== moneyFingerprint(q)) {
    out.push("The money on this quote changed after the custom payment wording was written, so it may be out of date. Rewrite or reset that wording.");
  }
  return out;
}

function generatedPaymentSentence(q) {
  const dep = getDeposit(q);
  if (dep.legacy) {
    return dep.type === "full_session"
      ? "A deposit equal to 100% of the session rate is due upon booking to secure the session date. Remaining balance is due after the session, before the 3D models are delivered."
      : "Payment in full after the session, before the 3D models are delivered.";
  }
  if (dep.type === "percent") {
    const total = calcTotals(q.lineItems, q.quoteDiscounts).total;
    const dollars = Math.round(total * dep.value) / 100; // cents-exact
    return `A deposit of ${dep.value}% (${fmtMoney(dollars)}) is due to confirm the session date. The balance is invoiced after the session.`;
  }
  if (dep.type === "amount") {
    return `A deposit of ${fmtMoney(dep.value)} is due to confirm the session date. The balance is invoiced after the session.`;
  }
  if (dep.type === "full_session") {
    return "A deposit equal to 100% of the session rate is due upon booking to secure the session date. The balance is invoiced after the session.";
  }
  return "Payment in full after the session.";
}

// A deposit in its stored shape, or null when the type and value don't make one.
export const sanitizeDeposit = (type, value) => {
  const v = Number(value);
  if (type === "none") return { type: "none", value: null };
  if (type === "percent" && Number.isFinite(v) && v > 0 && v <= 100) return { type: "percent", value: v };
  if (type === "amount" && Number.isFinite(v) && v > 0) return { type: "amount", value: v };
  if (type === "full_session") return { type: "full_session", value: null };
  return null;
};

/* ═══ DOCUMENT SECTIONS ═══ */
// A section is "locked on" when it's an always-on legal clause, or when it's the
// cancellation clause and the quote carries a deposit. Single source of truth so
// the builder editor and the printed PDF can never disagree about what shows.
export const isSectionLocked = (q, key) =>
  !!SECTION_DEFS[key]?.alwaysOn || (key === "cancellation" && getDeposit(q).type !== "none");

// Whether a section actually prints. Locked sections always print regardless of
// their stored `enabled` flag (guards against legacy/partial quote data where an
// always-on clause was saved disabled). `defaultOn` sections print unless the
// operator explicitly stored enabled:false (absent entry = on, so legacy quotes
// are unaffected). Everything else is opt-in.
export const isSectionOn = (q, key) => {
  if (isSectionLocked(q, key)) return true;
  if (SECTION_DEFS[key]?.defaultOn) return q?.sections?.[key]?.enabled !== false;
  return !!q?.sections?.[key]?.enabled;
};

// Section body text, falling back to the catalog default when a quote's sections
// map is missing or partial.
const sectionText = (q, key) => q?.sections?.[key]?.text ?? SECTION_DEFS[key]?.defaultText ?? "";

// Travel clause (2026-09-04). The catalog default says travel "will be quoted separately",
// which contradicted every quote that carried travel line items (the 9/12 quote printed
// $500 of travel lines under that sentence). When the stored text is still one of the two
// catalog sentences, or empty, the clause follows the line items: included as a line item
// when a travel line exists, quoted separately otherwise. Text the operator typed wins.
export const TRAVEL_LINE_IDS = ["travel_local", "travel_mid", "truck"];
export const hasTravelLineItem = (lineItems) => (lineItems || []).some((li) => TRAVEL_LINE_IDS.includes(li?.catalogId));
export function travelClauseText(q) {
  const def = SECTION_DEFS.travel;
  const stored = String(q?.sections?.travel?.text ?? "").trim();
  const catalog = new Set([def.defaultText, def.includedText].map((s) => String(s).trim()));
  if (stored && !catalog.has(stored)) return stored;
  return hasTravelLineItem(q?.lineItems) ? def.includedText : def.defaultText;
}

// Deliverables turnaround lines (2026-09-04). The generic sentence promises raw models
// from processing approval, which is photogrammetry language: on a LiDAR-only quote it
// printed right above the LiDAR turnaround line and contradicted it. It now prints when
// the quote has no estimator work at all (every array quote is unchanged), or when a
// LiDAR or drone quote also carries catalog work that is not estimator work, travel, or
// the drone permit line (array, object, Leo). Custom lines on an estimator quote do not
// bring it back. The LiDAR line prints whenever a LiDAR processing line exists, and the
// drone line after it whenever a drone processing line exists. The Turnaround section
// checkbox still gates the whole block, and the builder preview and the PDF both read
// this list, so they cannot differ.
const NON_MODEL_CATALOG_IDS = new Set([...TRAVEL_LINE_IDS, "drone_permit"]);
export const hasArrayCatalogWork = (lineItems) => (lineItems || []).some((li) => {
  // Lower-cased before the set lookup, as isEstimatorCatalogId is, so a drifted
  // "TRAVEL_LOCAL" is still travel and not counted as array work.
  const id = String(li?.catalogId ?? "").trim().toLowerCase();
  return !!id && !isEstimatorCatalogId(id) && !NON_MODEL_CATALOG_IDS.has(id);
});
export const showsArrayTurnaround = (lineItems) => !hasEstimatorLineItem(lineItems) || hasArrayCatalogWork(lineItems);
export const arrayTurnaroundText = (q) => {
  // Legacy quotes stored turnaround in WEEKS with no unit flag; new quotes tag "businessDays".
  // A quote with no turnaround value uses the 3–5 default, which is business days.
  const value = q?.turnaroundTBD ? "TBD" : String(q?.turnaround || "3-5").replace("-", "–");
  // Exactly "1" is singular; a range like "1-2" stays plural.
  const one = !q?.turnaroundTBD && String(q?.turnaround || "").trim() === "1";
  const unit = (q?.turnaroundUnit === "businessDays" || !q?.turnaround)
    ? (one ? "business day" : "business days")
    : (one ? "week" : "weeks");
  return wordingOr(q, "rawModels", `Raw models within ${value} ${unit} from processing approval.`);
};
// Processing-only wording (2026-09-04): when EVERY processing line of a family on the
// quote was priced with the data already in hand (mode "processingOnly", stamped on the
// audit block), its turnaround runs from receipt of that data, not from capture. The
// lines that carry both a mode and a turnaround are the capture and processing lines (v2)
// and archived v1 lines; rush lines carry neither and have no say.
const allProcessingOnly = (lineItems, key) => {
  const audits = (lineItems || []).map((it) => it?.[key]).filter((a) => a && a.mode && Number.isFinite(a.processingDays) && a.processingDays > 0);
  return audits.length > 0 && audits.every((a) => a.mode === "processingOnly");
};
export const lidarProcessingOnly = (lineItems) => allProcessingOnly(lineItems, "lidar");
export const droneProcessingOnly = (lineItems) => allProcessingOnly(lineItems, "drone");
export const lidarTurnaroundText = (days, processingOnly = false) =>
  `LiDAR processing (registration + textured OBJ): ~${days} business day${days === 1 ? "" : "s"} from ${processingOnly ? "receipt of the scans" : "capture"}.`;
export const droneTurnaroundText = (days, processingOnly = false) =>
  `Drone photogrammetry processing: ~${days} business day${days === 1 ? "" : "s"} from ${processingOnly ? "receipt of the stills" : "capture"}.`;
export const turnaroundLines = (q) => {
  if (!isSectionOn(q, "turnaround")) return [];
  const items = q?.lineItems || [];
  const out = [];
  // Removed wording ("") prints no line at all.
  if (showsArrayTurnaround(items) && arrayTurnaroundText(q)) out.push(arrayTurnaroundText(q));
  const days = lidarProcessingDays(items);
  if (days) out.push(lidarTurnaroundText(days, lidarProcessingOnly(items)));
  const droneDays = droneProcessingDays(items);
  if (droneDays) out.push(droneTurnaroundText(droneDays, droneProcessingOnly(items)));
  return out;
};

// Client documents never carry em dashes (house style). Applied to every string
// the document builders emit, so operator- and AI-typed section text is covered
// too, not just our literals.
// Leading/trailing em dashes are dropped outright (a ", " there would strand
// punctuation); interior ones become a comma join.
export const stripEmDashes = (s) => String(s ?? "")
  .replace(/^\s*—+\s*/, "")
  .replace(/\s*—+\s*$/, "")
  .replace(/\s*—\s*/g, ", ")
  .replace(/—/g, ", ");

/* ═══ QUOTE SECTION TEXT (used by live preview + PDF regen) ═══ */
export function buildDeliverablesText(q) {
  // The lowpoly branch promises a reconstructed model, so it only applies while
  // processing is part of the job (turnaround section on). A capture-only quote
  // falls through to the generic path even if a lowpoly line is present.
  const hasLowPoly = (q.lineItems || []).some((item) => item.catalogId === "lowpoly_session" || item.catalogId === "lowpoly_body");
  if (hasLowPoly && isSectionOn(q, "turnaround")) {
    const customText = (q.deliverableText || "").trim();
    const scopeText = customText && customText !== DEFAULT_QUOTE_DELIVERABLE_TEXT
      ? customText.replace(/[.!?]+$/, "")
      : "Raw reconstructed full-body model, up to 1M polygons, delivered as OBJ or FBX";
    return [
      `${scopeText}.`,
      wordingOr(q, "lowPolyExclusions", "Cleanup, decimation, and retopology are not included."),
    ].filter(Boolean).map(stripEmDashes);
  }

  const deliverableText = (q.deliverableText || "To be determined").trim().replace(/[.!?]+$/, "");
  const rawFiles = wordingOr(q, "rawFiles", "All deliverables are raw files unless otherwise stated.");
  const p = [rawFiles ? `${deliverableText}. ${rawFiles}` : `${deliverableText}.`];
  if (isSectionOn(q, "fileFormats")) p.push(sectionText(q, "fileFormats"));
  return p.map(stripEmDashes);
}

export function buildTermsText(q) {
  const p = [];
  // Pro bono (2026-09-11): nothing is owed, so the payment sentence (and with it
  // any deposit line, which buildPaymentSentence folds into that sentence) is
  // suppressed. Every other clause prints as it does today.
  if (!proBonoPrints(q)) {
    const paymentSuffix = isSectionOn(q, "payment") ? " " + sectionText(q, "payment") : "";
    // Custom wording may remove the sentence, so what's left can't open with a space.
    const payment = buildPaymentSentence(q);
    const line = payment ? payment + paymentSuffix : paymentSuffix.trim();
    if (line) p.push(line);
  }
  if (isSectionOn(q, "travel")) p.push(travelClauseText(q));
  if (isSectionOn(q, "cancellation")) p.push(sectionText(q, "cancellation"));
  if (isSectionOn(q, "projectTerms") && sectionText(q, "projectTerms").trim()) p.push(sectionText(q, "projectTerms"));
  if (isSectionOn(q, "dataDelivery")) p.push(sectionText(q, "dataDelivery"));
  if (isSectionOn(q, "expiration")) p.push(sectionText(q, "expiration"));
  return p.map(stripEmDashes);
}

export function buildPoliciesText(q) {
  const p = [];
  if (isSectionOn(q, "nda")) p.push(sectionText(q, "nda"));
  const usage = wordingOr(q, "usageRights", "New York Capture holds no usage rights to any client data, whether purchased or not.");
  if (usage) p.push(usage);
  return p.map(stripEmDashes);
}

/* ═══ CRM MATCHING ═══ */
/* The tiered matcher moved to `identity.js` (2026-08-26) so every path that asks
   "is this the same person" answers it the same way. It is re-exported here
   because the builder and its tests already import it from this module. */
export { matchContactsToCRM };

/* ── Contact-match review (Step 4 Slice 2) ──────────────────────────────────
   matchContactsToCRM embeds whole CRM records in `.match`; persisting those on
   the quote doc would bloat it and duplicate CRM data. buildContactMatches keeps
   only what the review UI needs (ids + names + the user's decision), so the lean
   shape is what gets saved via the quote object. The decision helpers are pure
   array transforms so the state machine can be unit-tested without the component.

   Entry shape: { extracted{name,email,url,role,source}, confidence,
                  matchId, matchName, origMatchId, origMatchName,
                  confirmed, ignored } */
export function buildContactMatches(extractedContacts, contacts, clients) {
  return matchContactsToCRM(extractedContacts, contacts, clients, {
    internalEmails: CRM_INTERNAL_EMAILS,
  }).map((m) => {
    const ec = m.extracted || {};
    const matchId = m.match?.id || null;
    const matchName = m.match?.name || null;
    return {
      extracted: {
        name: ec.name || "",
        email: ec.email || "",
        url: ec.url || "",
        role: ec.role || "contact",
        source: ec.source || "",
      },
      confidence: m.confidence || "low",
      matchId,
      matchName,
      origMatchId: matchId,
      origMatchName: matchName,
      confirmed: false,
      ignored: false,
    };
  });
}

// "Link" — accept the matched CRM record.
export function confirmContactMatch(list, i) {
  return (list || []).map((m, idx) => (idx === i ? { ...m, confirmed: true, ignored: false } : m));
}
// "New" — reject the match and create a fresh record on book.
export function rejectContactMatch(list, i) {
  return (list || []).map((m, idx) =>
    idx === i ? { ...m, matchId: null, matchName: null, confirmed: true, ignored: false } : m);
}
// "No Link" — skip this contact entirely (no CRM write).
export function ignoreContactMatch(list, i) {
  return (list || []).map((m, idx) => (idx === i ? { ...m, ignored: true, confirmed: true } : m));
}
// Undo — restore the original match and re-open the decision.
export function undoContactMatch(list, i) {
  return (list || []).map((m, idx) =>
    idx === i ? { ...m, confirmed: false, ignored: false, matchId: m.origMatchId, matchName: m.origMatchName } : m);
}
// "Link All Matches" — confirm only rows that actually HAVE a CRM match. Rows
// with no match are left open so the operator must make an explicit New / No Link
// call on each; a bulk button must not silently promote unmatched people to New.
export function confirmAllContactMatches(list) {
  return (list || []).map((m) => (m.ignored || !m.matchId ? m : { ...m, confirmed: true }));
}
// Soft-nudge gate: true when there's nothing left to decide.
export function allContactMatchesConfirmed(list) {
  return !list || list.length === 0 || list.every((m) => m.confirmed || m.ignored);
}

// A quote needs a CRM contact (2026-10-26): a typed name alone booked the
// FS UK job with no linked contact, because the job only links an explicit id or
// an exact-name match. Linked means the Contact picker holds an id, or the
// operator confirmed, in the contact-match panel, a named person (not a cc, not
// the company row) who is the quote's contact: the row's name or its CRM match
// is the name in the Contact field. saveQuote turns that row into the quote's
// contactEntityId. A confirmed row for someone else no longer counts once a
// different name is typed into the Contact field.
/* ═══ THE RATE SHEETS' OWN PROCESSING NOTES ═══
   QuotePrintable prints these, and the AI box's preview reads them, so "no longer prints"
   can't be claimed for words a rate sheet still carries. The rest of a sheet is fixed
   tables the preview doesn't read, and it says so. */
export const ARRAY_SHEET_FOOT = "Standard processing turnaround: 3–5 business days. Site requirements for on-location days are provided separately. Equipment specifications on the following page.";

const CAPTURE_ONLY_SHEET_NOTE = "Capture only on this quote. Processing can be added later at the rates above.";

export const lidarSheetNote = (lineItems) => {
  const days = lidarProcessingDays(lineItems);
  const source = lidarProcessingOnly(lineItems) ? "receipt of the scans" : "capture";
  return days
    ? `Processing turnaround: ~${days} business day${days === 1 ? "" : "s"} from ${source}. Each quoted scan set includes ${LIDAR_MODEL.processing.graceSetups} positions above the quoted count.`
    : CAPTURE_ONLY_SHEET_NOTE;
};

export const lidarSheetNoteV1 = (lineItems) =>
  `Processing turnaround: ~${lidarProcessingDays(lineItems) || LIDAR_MATRIX.defaultProcessingDays} business days from capture (scales with scope).`;

export const droneSheetNote = (lineItems) => {
  const days = droneProcessingDays(lineItems);
  const source = droneProcessingOnly(lineItems) ? "receipt of the stills" : "capture";
  return days
    ? `Processing turnaround: ~${days} business day${days === 1 ? "" : "s"} from ${source}. Each quote includes the quoted acres plus ${Math.round(DRONE_MODEL.processing.graceShare * 100)} percent before a change order.`
    : CAPTURE_ONLY_SHEET_NOTE;
};

/** The notes on the rate sheets this quote prints. Empty means no rate sheet prints. */
export function rateSheetNotes(q) {
  const items = q?.lineItems;
  const notes = [];
  if (showsArraySheet(q)) notes.push(ARRAY_SHEET_FOOT);
  if (hasLidarLineItem(items)) notes.push(lidarModelVersion(items) === "v2" ? lidarSheetNote(items) : lidarSheetNoteV1(items));
  if (hasDroneLineItem(items)) notes.push(droneSheetNote(items));
  return notes;
}

// An active quote: shown in Quotes (not Drafts). An assessment quote stays a
// draft record but is active. One predicate for the lists, Save draft and the
// draft preserver, so none of them treats an active quote as a draft.
export function isActiveQuoteRecord(sq) {
  return !!sq && (!sq.draft || !!sq.fromAssessment || sq.jobStatus === "booked");
}

export const LINK_CONTACT_MESSAGE = "Link the contact first: pick them in the Contact field, add them as a new contact, or confirm them under Contacts Found.";

/** Why this quote can't be saved as an active quote, the same words wherever it's asked:
 *  the preview's Ingest, the Unsaved dialog's Save, and Save draft over an active quote. */
export function quoteSaveBlocker(quote) {
  const linked = quoteHasLinkedContact(quote);
  if (!linked) return LINK_CONTACT_MESSAGE;
  const complete = quote?.clientName?.trim() && quote?.contactName?.trim() && (quote?.lineItems || []).some((li) => li.description?.trim());
  if (!complete) return "Add a client, a contact, and at least one line item first.";
  // A picture with no stored file behind it (its upload still running, or failed) would
  // save as an attachment that only this computer can show. Read from the quote itself,
  // so the check holds wherever the save is asked for, the Unsaved dialog included.
  const unsent = (quote?.attachments || []).find((a) => a && !a.url && !a.storagePath);
  if (unsent) return `${unsent.name || "An attachment"} hasn't finished uploading, or its upload failed. Open the quote in the builder and retry or remove it before saving.`;
  return "";
}


// Why this quote can't be written as an active quote, or "" when it can.
// - An empty contacts list means they never loaded (the CRM is never empty):
//   no link can be checked, and a confirmed row would make a duplicate.
// - A stored link must still point at a contact; so must a confirmed row's match.
// - requireId: the write path has no CRM commit (Save draft over an active
//   quote, version restore), so only a stored link counts there.
export function quoteContactBlocker(quote, contacts, { requireId = false } = {}) {
  if (!quoteHasLinkedContact(quote)) return LINK_CONTACT_MESSAGE;
  const list = contacts || [];
  if (list.length === 0) return "Your contacts haven't loaded, so the contact link can't be checked. Reload the page and try again.";
  const id = quote.contactEntityId;
  if (id) {
    if (!list.some((c) => c.id === id)) return "The linked contact is no longer in your contacts. Pick them again.";
    return "";
  }
  if (requireId) return "Ingest the quote to add the contact you confirmed, or pick them in the Contact field.";
  const row = confirmedPrimaryMatch(quote);
  if (row.matchId && !list.some((c) => c.id === row.matchId)) {
    return "The contact you confirmed under Contacts Found is no longer in your contacts. Review it again.";
  }
  return "";
}

// One person's link and email from their CRM record. The name is the job's
// Contact field when it has one (the quote then names the person the way the
// job does, so the next amendment still matches them after a CRM rename), else
// the record's name.
const contactFieldsFrom = (record, jobName = "") => ({
  contactEntityId: record.id,
  contactName: String(jobName || "").trim() || record.name || "",
  contactEmail: primaryContactEmail(record),
});

// The contact a change order starts with, as one person: the job's current
// contact of record; else the original quote's linked contact, but only when
// the job names nobody else; else the job's typed name with no link (the
// builder then asks for one).
export function changeOrderContact(job, originalQuote, contacts) {
  const list = contacts || [];
  const jobRecord = list.find((c) => c.id === jobPrimaryContactId(job, list, originalQuote));
  if (jobRecord) return contactFieldsFrom(jobRecord, job?.contactName);
  const typed = normalizeName(job?.contactName || "");
  const sameName = !typed || normalizeName(originalQuote?.contactName || "") === typed;
  const originalRecord = originalQuote?.contactEntityId ? list.find((c) => c.id === originalQuote.contactEntityId) : null;
  // The original quote's person, with their current name and email.
  if (sameName && originalRecord) return contactFieldsFrom(originalRecord, job?.contactName || originalQuote.contactName);
  return { contactEntityId: null, contactName: job?.contactName || originalQuote?.contactName || "", contactEmail: "" };
}

// Amending a booked quote: the job's current contact of record wins (name, link,
// email as one person). When the job names someone else who isn't linked, the
// quote takes that name with no link, so the builder asks for the contact
// instead of keeping the old person. Otherwise the quote keeps its own link (a
// stale one is left for the builder to clear and ask for again).
export function amendContact(quote, job, contacts) {
  const record = (contacts || []).find((c) => c.id === jobPrimaryContactId(job, contacts, quote));
  if (record) {
    // Same person: still refresh the name and email if the record changed.
    const fields = contactFieldsFrom(record, job?.contactName);
    const same = fields.contactEntityId === quote?.contactEntityId && fields.contactName === quote?.contactName
      && fields.contactEmail === (quote?.contactEmail || "");
    // The phone follows the person too (one person: name, link, email, phone), and a
    // changed phone alone is a change. The same person with no CRM phone keeps the phone
    // the quote holds (an inquiry's); someone else's record replaces it.
    // The same person as writeContact (quoteParty.js) reads it: already linked as them, or
    // an unlinked name (a legacy booked quote's, an inquiry's) that is their name.
    const samePerson = fields.contactEntityId === quote?.contactEntityId
      || (!quote?.contactEntityId && !!normalizeName(quote?.contactName || "") && normalizeName(quote?.contactName || "") === normalizeName(record.name || ""));
    const phone = primaryContactPhone(record) || (samePerson ? (quote?.contactPhone || "") : "");
    const phoneChange = (quote?.contactPhone || "") === phone ? {} : { contactPhone: phone };
    if (same) return phoneChange;
    return { ...fields, ...phoneChange };
  }
  const typed = normalizeName(job?.contactName || "");
  if (typed && normalizeName(quote?.contactName || "") !== typed) {
    return { contactEntityId: null, contactName: job.contactName.trim(), contactEmail: "", ...(quote?.contactPhone ? { contactPhone: "" } : {}) };
  }
  return {};
}

// The contact fields a version restore writes: the version's own when they
// pass, else the quote's current ones; null when neither has a valid link. A
// version from before contactEmail was versioned takes the email from the CRM
// record, so the name, link and email are one person.
export function restoreContactFields(saved, restored, contacts, version = null) {
  const has = (field) => !!version && Object.prototype.hasOwnProperty.call(version, field);
  if (!quoteContactBlocker(restored, contacts, { requireId: true })) {
    const record = (contacts || []).find((c) => c.id === restored.contactEntityId);
    const fields = {};
    if (!has("contactEmail")) fields.contactEmail = primaryContactEmail(record);
    if (!has("contactPhone")) {
      // A version from before the phone was versioned: the same person keeps their
      // current phone, someone else takes the CRM's, so a restore carries one person.
      const samePerson = (saved?.contactEntityId || null) === (restored.contactEntityId || null);
      const phone = (samePerson ? saved?.contactPhone : "") || primaryContactPhone(record);
      if (phone !== (restored.contactPhone || "")) fields.contactPhone = phone;
    }
    return fields;
  }
  if (!quoteContactBlocker(saved, contacts, { requireId: true })) {
    const phone = (saved.contactPhone || "") === (restored.contactPhone || "") ? {} : { contactPhone: saved.contactPhone || "" };
    return { contactEntityId: saved.contactEntityId, contactName: saved.contactName || "", contactEmail: saved.contactEmail || "", ...phone };
  }
  return null;
}

// The client account a saved quote falls back to when none was picked: the
// typed Client name when it names exactly one account, else the contact's email
// or domain. The contact may work somewhere else (a producer at an agency
// quoting for a museum), so their email must not outrank the client the quote
// names. A client left unlinked on purpose stays unlinked: nothing to fall back to.
export function quoteClientMatch(quote, clients) {
  if (quote?.clientUnlinked) return null;
  const byName = quote?.clientName ? resolveClient({ name: quote.clientName }, clients).record : null;
  return byName || resolveClient({ name: quote?.clientName, email: quote?.contactEmail }, clients).record;
}

// One record per id from several lists, the last one winning: the loaded
// contacts first, then the commit's writes in order, so a person updated twice
// in one save resolves to their final record.
export function latestById(...lists) {
  const byId = new Map();
  for (const list of lists) for (const r of list || []) if (r && r.id) byId.set(r.id, r);
  return [...byId.values()];
}

// The saved quote's email follows its linked contact: a contact linked through
// Contacts Found (the commit's id) brings their email, so the name, link and
// email are one person; a contact with no email clears a stale one. Looks up
// the exact linked id only, never a fallback.
export function withLinkedContactEmail(record, contacts) {
  const linked = record?.contactEntityId ? (contacts || []).find((c) => c.id === record.contactEntityId) : null;
  // The phone is not replaced here: it follows the person when they are picked, amended
  // or restored, and an ordinary save keeps what the quote holds (a restored number).
  return linked ? { ...record, contactEmail: primaryContactEmail(linked) } : record;
}

// "New" on a contact row clears its suggested CRM match. When the Contact field
// held that match's name (e.g. "Rhys" for the AI's "Rhys Perkins"), the field
// takes the row's own name so the row stays the quote's contact; otherwise the
// field is left as it is (or filled when empty, contactNameFromConfirmed).
export function contactNameAfterNew(quote, row, nextMatches) {
  // A contact picked in the Contact field is the quote's contact: New on an AI
  // row then only adds that row's person, and the field (and link) stay.
  if (quote?.contactEntityId) return quote.contactName || "";
  const ownName = String(row?.extracted?.name || "").trim();
  const namedByMatch = row && (row.extracted?.role || "contact") === "contact" && row.matchName && ownName
    && normalizeName(row.matchName) === normalizeName(quote?.contactName || "");
  return namedByMatch ? ownName : contactNameFromConfirmed(quote, nextMatches);
}

// A confirmed primary row with an empty Contact field fills the field from the
// row, so confirming the AI's contact is enough to link the quote.
export function contactNameFromConfirmed(quote, matches) {
  if (String(quote?.contactName || "").trim()) return quote.contactName;
  const row = (matches || []).find((m) => (
    m && m.confirmed && !m.ignored && (m.extracted?.role || "contact") === "contact"
    && String(m.extracted?.name || "").trim()
  ));
  return row ? row.extracted.name.trim() : (quote?.contactName || "");
}

// The job's contact of record, for a change order or an amendment. Booking
// links cc'd people with the same "contact" role, and replacing the job's
// contact appends the new one, so list order says nothing: the contact of
// record is the linked person named in the job's Contact field. A job that
// names someone not linked (changed to a person not yet in the CRM, or a
// renamed record) has no answer: null, never another linked person. Only a
// job with no contact name falls back to its one linked contact. Null means
// the builder asks for the contact.
export function jobPrimaryContactId(job, contacts, quote = null) {
  // seededLinkedContacts reads older jobs' linkedEntities too. The named person
  // counts under any role (their link may say Producer); only the nameless
  // fallback is limited to the "contact" role.
  const links = seededLinkedContacts(job).filter((l) => l && l.contactId);
  const typed = normalizeName(job?.contactName || "");
  if (typed) {
    const named = links.map((l) => l.contactId)
      .find((id) => normalizeName((contacts || []).find((c) => c.id === id)?.name) === typed);
    if (named) return named;
    // The CRM record was renamed since booking: the quote's linked person is
    // still the one, when the job links them and both carry the same name.
    const qid = quote?.contactEntityId;
    if (qid && links.some((l) => l.contactId === qid) && normalizeName(quote.contactName || "") === typed) return qid;
    return null;
  }
  const ids = links.filter((l) => (l.role || "contact") === "contact").map((l) => l.contactId);
  return ids.length === 1 ? ids[0] : null;
}

// Camera Array rate sheet attaches whenever the quote carries array work (studio
// sessions, on-location days, head/body scans) — same auto-attach rule as LiDAR.
// The legacy includePricingSheet toggle still forces it on for edge cases.
// Legacy filmtv ids included: retired from the catalog, but old quotes carrying them
// are still array work and should attach the sheet. An EXPLICIT includePricingSheet:false
// (operator unchecked the box) suppresses the sheet — auto-attach only fills the default.
// torso_scan / other_body are LEGACY-ONLY here (retired 2026-07-06): new quotes remap them
// to leo_torso / leo_other, which are correctly absent so handheld work skips the array sheet.
const ARRAY_SHEET_IDS = ["half_art", "full_art", "half_filmtv", "full_filmtv", "partial_mobile", "full_onlocation", "head_scan", "body_scan", "torso_scan", "other_body"];
export const showsArraySheet = (q) => (q?.includePricingSheet === false
  ? false
  : ((q?.lineItems || []).some((li) => ARRAY_SHEET_IDS.includes(li.catalogId)) || resolveVolumePricing(q).show));
